/**
 * AXOIX API Client
 * Axios-based HTTP client with interceptors and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_CONFIG } from "./config";

// Custom error type for API errors
export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  response?: { data: any }; // Preserve original response for detailed error handling
}

// Create main API client instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Create RAG service client instance
export const ragClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.RAG_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor - add API key and auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token from localStorage if available
    const token = localStorage.getItem("axoix_auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    config.headers["X-Request-Time"] = new Date().toISOString();

    return config;
  },
  (error: AxiosError) => {
    console.error("[API] Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === "development") {
      console.log(`[API] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
    }
    return response;
  },
  async (error: AxiosError) => {
    const apiError: ApiError = {
      message: "An unexpected error occurred",
      status: error.response?.status || 500,
    };

    if (error.response) {
      // Server responded with error
      const data = error.response.data as Record<string, unknown>;
      apiError.message = (data?.message as string) || (data?.detail as string) || error.message;
      apiError.code = data?.code as string;
      apiError.details = data?.details as Record<string, unknown>;

      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem("axoix_auth_token");
          apiError.message = "Session expired. Please log in again.";
          break;
        case 403:
          apiError.message = "You don't have permission to perform this action.";
          break;
        case 404:
          apiError.message = "The requested resource was not found.";
          break;
        case 409:
          apiError.message = (data?.message as string) || "A conflict occurred with the current state.";
          break;
        case 422:
          // Preserve full response for validation error details
          apiError.response = { data: error.response.data };
          // Try to extract validation errors
          if (Array.isArray(data?.detail)) {
            const errors = (data.detail as any[]).map((d) => d.msg || d.message).join("; ");
            apiError.message = errors || "Invalid data provided. Please check your input.";
          } else if (typeof data?.detail === 'string') {
            apiError.message = data.detail as string;
          } else {
            apiError.message = "Invalid data provided. Please check your input.";
          }
          break;
        case 429:
          apiError.message = "Too many requests. Please try again later.";
          break;
        case 500:
          // Keep the backend's specific detail (already extracted above) when present —
          // only fall back to the generic message if the server gave us nothing useful.
          if (!data?.detail && !data?.message) {
            apiError.message = "Server error. Our team has been notified.";
          }
          break;
      }
    } else if (error.request) {
      // Request made but no response received
      apiError.message = "Unable to connect to server. Please check your internet connection.";
      apiError.status = 0;
      apiError.code = "NETWORK_ERROR";
    }

    // Log errors in development
    if (process.env.NODE_ENV === "development") {
      console.error("[API] Error:", apiError);
    }

    return Promise.reject(apiError);
  }
);

// Helper function to check if API is available
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get("/health");
    return response.status === 200;
  } catch {
    return false;
  }
};

// Helper function to check RAG service health
export const checkRagHealth = async (): Promise<boolean> => {
  try {
    const response = await ragClient.get("/health");
    return response.status === 200;
  } catch {
    return false;
  }
};

export default apiClient;
